import { Action, createAsyncAction, createReducer } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, of, from } from 'rxjs';
import { catchError, map, withLatestFrom, filter, exhaustMap } from 'rxjs/operators';
import { produce } from 'immer';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { toReduxError, type ReduxError, GENERIC_FETCH_ERROR_MESSAGE } from '@shared/errors';
import type { GetProductsResponse, ProductType } from '@dtp/membership-service-types';
import { TypedEpic } from '../types';

// Actions
export enum Actions {
  GET_PRODUCTS = 'naf/GET_PRODUCTS',
  GET_PRODUCTS_SUCCESS = 'naf/GET_PRODUCTS_SUCCESS',
  GET_PRODUCTS_FAIL = 'naf/GET_PRODUCTS_FAIL',
  GET_PRODUCTS_CANCEL = 'naf/GET_PRODUCTS_CANCEL',
}

export interface State {
  data: {
    products: Record<ProductType['productNumber'], ProductType>;
  };
  isUpdating: boolean;
  hasFetched: boolean;
  errorState?: any;
}

export const initialState: State = {
  data: {
    products: {},
  },
  isUpdating: false,
  hasFetched: false,
};

export const actions = {
  getProducts: createAsyncAction(
    Actions.GET_PRODUCTS, // request payload creator
    Actions.GET_PRODUCTS_SUCCESS, // success payload creator
    Actions.GET_PRODUCTS_FAIL, // failure payload creator
    Actions.GET_PRODUCTS_CANCEL, // optional cancel payload creator
  )<string | undefined, GetProductsResponse, ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getProducts.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
    }),
  )
  .handleAction(actions.getProducts.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.hasFetched = true;
      draftState.data.products = (action.payload?.customerProducts?.eligibleProducts ?? []).reduce(
        (acc: Record<ProductType['productNumber'], ProductType>, cur) => {
          acc[cur.productNumber] = cur;
          return acc;
        },
        {},
      );
    }),
  )
  .handleAction(actions.getProducts.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.hasFetched = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getProducts.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
    }),
  );

export const getProductsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_PRODUCTS),
    withLatestFrom(state$),
    filter(([, state]) => !state.products.hasFetched),
    exhaustMap(() =>
      from(
        apiClient.get<GetProductsResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.products,
          skipNafHeaders: true,
        }),
      ).pipe(
        map((response) => actions.getProducts.success(response.data)),
        catchError((err) => of(actions.getProducts.failure(toReduxError(err, GENERIC_FETCH_ERROR_MESSAGE)))),
      ),
    ),
  );

export const epics: TypedEpic[] = [getProductsEpic];
